import {
  InlineStack,
  Text,
  BlockStack,
  Tooltip,
  Button,
  TextField,
  Popover,
  Listbox,
  Icon,
  Scrollable,
  EmptySearchResult,
  Loading,
  Box,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { Property, Patient } from "../../types";
import { useParams } from "react-router-dom";
import { EditIcon, SearchIcon } from "@medentic/polaris-icons";
import styles from "./TopBanner.module.css";
import { UserMetaResponse } from "@shared/types/user";
import { RootState } from "@app/store/store";
import { useSelector } from "react-redux";
import PatientCellRenderer from "../PatientCellRenderer";
import useTags from "@shared/hooks/common/useTags";

interface TopBannerProps {
  patient: Patient;
}

const EMPTY_FEATURED_PROPERTIES: string[] = [];

function TopBanner({ patient }: TopBannerProps) {
  const { t } = useTranslation();
  const { GET, PATCH } = useApiRequest();
  const queryClient = useQueryClient();
  const { clinicId } = useParams<{ clinicId: string }>();
  const { tags } = useTags(clinicId ?? "");
  const tagsById = useMemo(
    () => new Map(tags.map((tag) => [tag.id, tag])),
    [tags]
  );

  const [activePopoverId, setActivePopoverId] = useState<string | null>(null);
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>(
    {}
  );
  const [activeFieldId, setActiveFieldId] = useState<string>("");
  const { data: userMeta } = useQuery<UserMetaResponse>({
    queryKey: ["userMeta"],
    queryFn: () => GET<UserMetaResponse>("/api/user/me/meta"),
  });
  const featuredProperties = useMemo(
    () => userMeta?.featuredPatientProperties ?? EMPTY_FEATURED_PROPERTIES,
    [userMeta?.featuredPatientProperties]
  );

  const obfuscateSensitiveData = useSelector(
    (state: RootState) => state.app.obfuscateSensitiveData
  );


  // Fetch available properties
  const { data: properties = [] } = useQuery<Property[]>({
    queryKey: ["patient-schema-properties"],
    queryFn: () =>
      GET(`/api/healthcare-companies/${clinicId}/patients/schema/properties`),
  });

  const filteredProperties = useMemo(() => {
    if (!activePopoverId) return properties;
    const searchQuery = searchQueries[activePopoverId] || "";

    return properties.filter(
      (prop) =>
        (prop.label?.toLowerCase()?.includes(searchQuery.toLowerCase()) ||
          prop.id?.toLowerCase()?.includes(searchQuery.toLowerCase())) &&
        // Only include properties that are either the current field or not in other featured fields
        (prop.name === activePopoverId ||
          !featuredProperties.includes(prop.name))
    );
  }, [properties, searchQueries, activePopoverId, featuredProperties]);

  const updateFeaturedPropertiesMutation = useMutation({
    mutationFn: async (newProperties: string[]) => {
      await PATCH("/api/user/me/meta", {
        featuredPatientProperties: newProperties,
        obfuscateSensitiveData: obfuscateSensitiveData,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userMeta"] });
    },
  });

  const handleFieldSelect = async (fieldId: string) => {
    const currentFields = [...featuredProperties];
    const index = currentFields.indexOf(fieldId);

    if (index !== -1) {
      // If field is already selected, do nothing
      return;
    } else if (currentFields.length < 4) {
      // If under limit, add new field
      currentFields.push(fieldId);
    } else {
      // If at limit, replace the currently active field with the new one
      const activeIndex = currentFields.indexOf(activePopoverId || "");
      if (activeIndex !== -1) {
        currentFields[activeIndex] = fieldId;
      }
    }

    // Update backend
    try {
      await updateFeaturedPropertiesMutation.mutateAsync(currentFields);
    } catch (error) {
      console.error("Failed to update featured properties:", error);
      // Revert local state on error
    }
  };

  const renderFieldValue = (fieldId: string, property?: Property) => {
    if (patient[fieldId] === null || patient[fieldId] === undefined) return "-";

    return (
      <PatientCellRenderer
        column={{
          name: property?.name ?? "",
          label: property?.label ?? "",
          type: property?.renderer?.type ?? "",
          id: property?.renderer?.id ?? "",
          visible: true,
          ...property,
        }}
        patient={patient}
        allowBadgeRefresh={true}
        tagsById={tagsById}
      />
    );
  };

  const handleSearchChange = (value: string) => {
    if (!activePopoverId) return;
    try {
      setSearchQueries((prev) => ({
        ...prev,
        [activePopoverId]: value || "",
      }));
    } catch (error) {
      console.error("Error updating search query:", error);
    }
  };

  const handlePopoverOpen = (fieldId: string) => {
    setActivePopoverId(fieldId);
    // Reset search when opening popover if no existing query
    if (!searchQueries[fieldId]) {
      setSearchQueries((prev) => ({
        ...prev,
        [fieldId]: "",
      }));
    }
  };

  const handlePopoverClose = () => {
    setActivePopoverId(null);
  };

  const handleActiveOptionChange = (_value: string, domId: string) => {
    setActiveFieldId(domId);
  };

  const listboxId = "PropertyListbox";

  return (
    <Box paddingInline="200">
      <InlineStack align="space-between">
        {updateFeaturedPropertiesMutation.isPending && <Loading />}
        {featuredProperties.map((fieldId) => {
          const property = properties.find((p) => p.name === fieldId);
          return (
            <BlockStack key={fieldId} gap="200">
              <div className={styles.fieldContainer}>
                <Tooltip
                  hasUnderline
                  content={property?.description ?? t("common.noDescription")}
                >
                  <Text as="p" variant="headingMd" fontWeight="bold">
                    {property?.label ?? fieldId}
                  </Text>
                </Tooltip>
                <div
                  className={styles.editButton}
                  data-active={activePopoverId === fieldId}
                >
                  <Popover
                    active={activePopoverId === fieldId}
                    activator={
                      <Button
                        icon={EditIcon}
                        variant="plain"
                        onClick={() => handlePopoverOpen(fieldId)}
                      />
                    }
                    onClose={handlePopoverClose}
                    preferredAlignment="left"
                    autofocusTarget="first-node"
                  >
                    <Popover.Pane fixed>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "310px",
                          height: "100%",
                        }}
                      >
                        <div style={{ padding: "12px" }}>
                          <TextField
                            clearButton
                            labelHidden
                            label={t("common.search")}
                            placeholder={t("common.searchFields")}
                            autoComplete="off"
                            value={searchQueries[fieldId] || ""}
                            prefix={<Icon source={SearchIcon} />}
                            ariaActiveDescendant={activeFieldId}
                            ariaControls={listboxId}
                            onChange={handleSearchChange}
                            onClearButtonClick={() => handleSearchChange("")}
                          />
                        </div>

                        <Scrollable
                          style={{
                            height: "292px",
                            padding: "var(--p-space-200) 0",
                          }}
                        >
                          <Listbox
                            enableKeyboardControl
                            customListId={listboxId}
                            onSelect={handleFieldSelect}
                            onActiveOptionChange={handleActiveOptionChange}
                          >
                            {filteredProperties.length > 0 ? (
                              filteredProperties.map((property) => (
                                <Listbox.Option
                                  key={property.name}
                                  value={property.name}
                                  selected={property.name === fieldId}
                                >
                                  <Listbox.TextOption
                                    selected={property.name === fieldId}
                                  >
                                    {property.label}
                                  </Listbox.TextOption>
                                </Listbox.Option>
                              ))
                            ) : (
                              <EmptySearchResult
                                title=""
                                description={t("common.noResults", {
                                  query: searchQueries[fieldId] || "",
                                })}
                              />
                            )}
                          </Listbox>
                        </Scrollable>
                      </div>
                    </Popover.Pane>
                  </Popover>
                </div>
              </div>
              {renderFieldValue(fieldId, property)}
            </BlockStack>
          );
        })}
      </InlineStack>
    </Box>
  );
}

export default TopBanner;
